import React, { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  PageLayout,
  SplitTable,
  SplitTableRef,
  DialogNotify,
  DeleteOrderIcon,
  ValidationToast,
  EmptyState,
  OrderConfirmationModal,
  IncomingOrderDetails,
  OrderPricingData,
} from "@components";
import { useOrdersByStatus } from "../hooks";
import { useWorkspace } from "../context/WorkspaceContext";
import { useUserSession } from "../context/UserContext";
import { useDataCache } from "@data";

import { Order, Customer } from "../models";
import { OrderType, OrderStatus } from "../types/order/IOrder";
import { ITableRow, ITableCell } from "../types/TableData";
import { Timestamp } from "../types";
import { getOrderType, formatGmailStyle } from "../helpers/orderFormat";
import { getPickupLocation, getDeliveryLocation } from "../helpers/orderData";
import {
  HiOutlineLocationMarker,
  HiOutlineTruck,
  HiOutlineOfficeBuilding,
  HiOutlineUser,
  HiInbox,
} from "react-icons/hi";
import {
  checkPricing,
  confirmOrder,
  deleteOrderApi,
  updateOrderDraft,
} from "../api/client";

const IncomingOrders: React.FC = () => {
  const { t } = useTranslation(["common", "navigation", "orders"]);

  const navigate = useNavigate();
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const { invalidateOrders } = useDataCache();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showDeleteOrderDialog, setShowDeleteOrderDialog] = useState(false);
  // Pricing state
  const [pricingData, setPricingData] = useState<OrderPricingData | null>(null);
  const [isPriceLoading, setIsPriceLoading] = useState(false);

  // Address validation error state
  const [showAddressValidationToast, setShowAddressValidationToast] =
    useState(false);
  const [addressValidationMessage, setAddressValidationMessage] =
    useState<string>("");

  // Function to navigate to edit page
  const handleEditOrder = () => {
    if (selectedOrder) {
      navigate(`/orders/edit/${selectedOrder.id}`);
    }
  };

  const splitTableRef = useRef<SplitTableRef>(null);
  const lastCleanupRef = useRef<string | null>(null);

  // Fetch orders with Draft status only using global hook for cost optimization
  const ordersQuery = useOrdersByStatus([OrderStatus.Draft]);
  const orders = useMemo(() => {
    const fetchedOrders = ordersQuery.data ?? [];
    // Sort orders by creation time (newest first)
    return fetchedOrders.sort((a: Order, b: Order) => {
      const timeA = a.createdAt?.toDate?.() || new Date(0);
      const timeB = b.createdAt?.toDate?.() || new Date(0);
      return timeB.getTime() - timeA.getTime(); // Descending order (newest first)
    });
  }, [ordersQuery.data]) as Order[];
  const loading = useMemo(() => ordersQuery.isLoading, [ordersQuery.isLoading]);

  // Clear selected order if it no longer exists in the orders list
  useEffect(() => {
    // Only run cleanup if we have a selected order and data is fully loaded
    if (!selectedOrder || loading) {
      return;
    }

    // Prevent duplicate cleanup for the same order
    if (lastCleanupRef.current === selectedOrder.id) {
      return;
    }

    // Check if selected order still exists in the current orders list
    if (orders && orders.length >= 0) {
      const orderStillExists = orders.some(
        (order) => order.id === selectedOrder.id,
      );
      if (!orderStillExists) {
        console.log(
          `Cleaning up selectedOrder ${selectedOrder.id} - no longer in list`,
        );
        lastCleanupRef.current = selectedOrder.id;
        setSelectedOrder(null);
        // Close split table view
        if (splitTableRef.current) {
          splitTableRef.current.setSplit(false);
        }
      }
    }
  }, [orders, selectedOrder, loading]);

  // Reset cleanup ref when no order is selected
  useEffect(() => {
    if (!selectedOrder) {
      lastCleanupRef.current = null;
    }
  }, [selectedOrder]);

  // Transform orders to table rows
  const tableRows: ITableRow[] = useMemo(() => {
    if (!orders) return [];

    return orders.map((order: Order) => {
      // Get customer name
      const customerName =
        Customer.getCustomerName(order.customer) ||
        t("common:unknown_customer");

      // Get pickup and delivery locations - ensure we have one of each
      const pickupLocation = getPickupLocation(order);
      const deliveryLocation = getDeliveryLocation(order);

      // Get customer email for display
      const customerEmail = Customer.getCustomerEmail(order.customer);

      const orderType = getOrderType(order);
      const isOrderPriceRequest = orderType === OrderType.PriceRequest;

      return {
        id: order.id,
        isActive: selectedOrder?.id === order.id,
        onRowClickData: order,
        cells: [
          {
            id: "customer",
            children: (
              <div className="flex items-center space-x-3 py-2 px-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-surface-muted dark:bg-lgb-grey-600">
                  {order.customer?.isCompany ? (
                    <HiOutlineOfficeBuilding className="w-4 h-4 text-secondary" />
                  ) : (
                    <HiOutlineUser className="w-4 h-4 text-secondary" />
                  )}
                </div>
                <div>
                  <div className="font-medium text-text">
                    {customerName}
                  </div>
                  <div className="text-sm text-gray-900 dark:text-gray-300">
                    {customerEmail || t("common:no_email")}
                  </div>
                </div>
              </div>
            ),
            showOnSmallScreen: true,
          },
          {
            id: "type",
            children: (
              <div className="py-2 px-2">
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    isOrderPriceRequest
                      ? "bg-success-muted text-success dark:bg-lgb-green-900/20 dark:text-lgb-green-200"
                      : "bg-brand-muted text-brand dark:bg-brand-muted/20 dark:text-lgb-blue-200"
                  }`}
                >
                  {isOrderPriceRequest
                    ? t("orders:price_request")
                    : t("orders:order")}
                </span>
              </div>
            ),
            hidden: !!selectedOrder,
          },
          {
            id: "route",
            children: (
              <div className="space-y-1 py-2 px-2">
                {pickupLocation && (
                  <div className="flex items-center space-x-2 text-sm">
                    <HiOutlineLocationMarker className="w-4 h-4 text-secondary" />
                    <span className="text-gray-900 dark:text-gray-100">
                      {pickupLocation.addressLine ||
                        pickupLocation.city ||
                        t("common:pickup")}
                    </span>
                  </div>
                )}
                {deliveryLocation && (
                  <div className="flex items-center space-x-2 text-sm">
                    <HiOutlineTruck className="w-4 h-4 text-secondary" />
                    <span className="text-gray-900 dark:text-gray-100">
                      {deliveryLocation.addressLine ||
                        deliveryLocation.city ||
                        t("common:delivery")}
                    </span>
                  </div>
                )}
              </div>
            ),
            hidden: !!selectedOrder,
          },
          {
            id: "date",
            children: (
              <div className="text-sm text-gray-900 dark:text-gray-100 py-2 px-2">
                {order.route && order.route[0]?.stopDate
                  ? Timestamp.toDisplayDate(order.route[0].stopDate)
                  : t("common:no_date")}
              </div>
            ),
            hidden: !!selectedOrder,
          },
          {
            id: "received",
            children: (
              <div className="text-sm text-gray-900 dark:text-gray-100 py-2 px-2">
                {formatGmailStyle(order.createdAt, t("common:no_date"))}
              </div>
            ),
            hidden: !!selectedOrder,
          },
        ],
      };
    });
  }, [orders, selectedOrder, t]);

  // Table headers
  const tableHeaders: ITableCell[] = [
    {
      id: "customer",
      children: t("common:customer"),
      showOnSmallScreen: true,
    },
    {
      id: "type",
      children: t("common:type"),
      hidden: !!selectedOrder,
    },
    {
      id: "route",
      children: t("common:route"),
      hidden: !!selectedOrder,
    },
    {
      id: "date",
      children: t("orders:assignment_date_short"),
      hidden: !!selectedOrder,
    },
    {
      id: "received",
      children: t("orders:received_short"),
      hidden: !!selectedOrder,
    },
  ];

  const handleRowClick = (order: Order | undefined) => {
    setSelectedOrder(order || null);
  };

  const handleApproveOrder = () => {
    if (selectedOrder) {
      setShowConfirmationModal(true);
    }
  };

  const handleConfirmOrder = async (
    selectedCustomer: Customer | null,
    driverId: string,
  ) => {
    if (!selectedOrder || !activeWorkspace || !userSession) return;

    setIsConfirming(true);
    try {
      // If a specific customer was selected, update the order with that customer's information
      if (selectedCustomer) {
        const updatedOrder = {
          ...selectedOrder,
          customer: {
            ...selectedCustomer,
            // Keep any specific fields from the original order customer that might be needed
            contactEmail:
              selectedCustomer.contactEmail ||
              selectedOrder.customer?.contactEmail,
            invoiceEmail:
              selectedCustomer.invoiceEmail ||
              selectedOrder.customer?.invoiceEmail,
          },
          customerId: selectedCustomer.id,
          route:
            selectedOrder.route?.map((routeItem) => ({
              ...routeItem,
              stopDate: routeItem.stopDate
                ? typeof routeItem.stopDate === "object" &&
                  "toDate" in routeItem.stopDate
                  ? routeItem.stopDate.toDate().toISOString().split("T")[0] // Convert Firebase Timestamp to YYYY-MM-DD
                  : routeItem.stopDate // Keep as is if already formatted
                : null,
            })) || [],
        };

        await updateOrderDraft(
          userSession.token.accessToken,
          activeWorkspace.workspaceId,
          selectedOrder.id,
          updatedOrder,
        );
      } else {
        // Create a new customer from the order's customer data
        const orderCustomer = selectedOrder.customer;
        if (orderCustomer) {
          const newCustomerData = {
            workspaceId: activeWorkspace.workspaceId,
            isCompany: orderCustomer.isCompany || false,
            name: orderCustomer.name || "",
            firstName: orderCustomer.firstName || "",
            lastName: orderCustomer.lastName || "",
            contactEmail:
              orderCustomer.contactEmail || orderCustomer.invoiceEmail || "",
            invoiceEmail:
              orderCustomer.invoiceEmail || orderCustomer.contactEmail || "",
            phoneNumber: orderCustomer.phoneNumber || "",
            organizationNumber: orderCustomer.organizationNumber || "",
            primaryAddress: orderCustomer.primaryAddress || null,
          };

          // Create the new customer
          const newCustomer = await Customer.create(
            activeWorkspace.workspaceId,
            newCustomerData,
          );

          if (newCustomer) {
            // Update the order with the newly created customer
            const updatedOrder = {
              ...selectedOrder,
              customer: {
                ...newCustomer,
                contactEmail:
                  newCustomer.contactEmail || orderCustomer.contactEmail,
                invoiceEmail:
                  newCustomer.invoiceEmail || orderCustomer.invoiceEmail,
              },
              customerId: newCustomer.id,
              route:
                selectedOrder.route?.map((routeItem) => ({
                  ...routeItem,
                  stopDate: routeItem.stopDate
                    ? typeof routeItem.stopDate === "object" &&
                      "toDate" in routeItem.stopDate
                      ? routeItem.stopDate.toDate().toISOString().split("T")[0] // Convert Firebase Timestamp to YYYY-MM-DD
                      : routeItem.stopDate // Keep as is if already formatted
                    : null,
                })) || [],
            };

            await updateOrderDraft(
              userSession.token.accessToken,
              activeWorkspace.workspaceId,
              selectedOrder.id,
              updatedOrder,
            );

            // Update cache to get the latest customers list and the updated order
            invalidateOrders();

            console.log("New customer created and order updated successfully");
          } else {
            throw new Error("Failed to create new customer");
          }
        }
      }

      // Confirm the order with the selected driver
      await confirmOrder(
        userSession.token.accessToken,
        activeWorkspace.workspaceId,
        selectedOrder.id,
        driverId,
      );

      // Reset local loading state and close modal before navigating
      setIsConfirming(false);
      setIsPriceLoading(false);
      setShowConfirmationModal(false);

      // Store order ID and redirect immediately
      const orderToRedirect = selectedOrder.id;

      // Refresh both the Firestore realtime reads and the API-paginated orders
      // list before landing on /orders, so the confirmed order shows up there.
      invalidateOrders();

      // Redirect immediately to orders page with confirmed parameter
      navigate(`/orders?id=${orderToRedirect}&confirmed=true`);
    } catch (error: any) {
      // Handle address validation errors specifically
      if (
        error?.response?.data?.data?.error?.type === "AddressValidationError"
      ) {
        const errorData = error.response.data.data.error;
        setAddressValidationMessage(
          t("orders:address_not_found", {
            stopNumber: errorData.stop_number,
            address: errorData.address || "Unknown address",
          }),
        );
        setShowAddressValidationToast(true);
      } else {
        // Do not present unrelated backend failures as address validation errors.
        const serverMessage = error?.response?.data?.error;
        setAddressValidationMessage(
          t("orders:confirmation_failed", {
            message:
              typeof serverMessage === "string" && serverMessage.trim()
                ? serverMessage
                : t("orders:confirmation_failed_unknown"),
          }),
        );
        setShowAddressValidationToast(true);
      }

      setIsConfirming(false);
      setIsPriceLoading(false);
      setShowConfirmationModal(false);
    }
  };

  const handleCloseConfirmationModal = () => {
    if (!isConfirming) {
      setShowConfirmationModal(false);
    }
  };

  const handleRejectOrder = () => {
    if (selectedOrder) {
      setIsRejecting(true);
      setShowDeleteOrderDialog(true);
    }
  };

  const deleteOrder = async () => {
    if (!selectedOrder) return;

    try {
      const orderInfo =
        Customer.getCustomerName(selectedOrder.customer) || "Ukjent kunde";

      // Store the order ID for cleanup
      const deletedOrderId = selectedOrder.id;

      // Close UI elements immediately BEFORE deletion
      setShowDeleteOrderDialog(false);
      setSelectedOrder(null);

      // Close split table view
      if (splitTableRef.current) {
        splitTableRef.current.setSplit(false);
      }

      // Delete via backend API to ensure cascading deletes
      await deleteOrderApi(
        userSession?.token.accessToken ?? "",
        activeWorkspace?.workspaceId ?? "",
        deletedOrderId,
      );

      // The Firestore listener picks up the delete, but the API-paginated
      // orders list must be invalidated explicitly.
      invalidateOrders();

      // Show success message
      console.log(`Ordre for ${orderInfo} ble slettet`);
    } catch (error) {
      console.error("Error deleting order:", error);
      // Optionally show error message
    } finally {
      setIsRejecting(false);
    }
  };

  // Fetch pricing when selected order changes
  useEffect(() => {
    const fetchOrderPrice = async () => {
      if (
        !selectedOrder ||
        !userSession?.token?.accessToken ||
        !activeWorkspace?.workspaceId
      ) {
        setPricingData(null);
        return;
      }

      setIsPriceLoading(true);
      try {
        const response = await checkPricing(
          userSession.token.accessToken,
          activeWorkspace.workspaceId,
          selectedOrder,
          true, // TODO: derive VAT registration status from customer/company if needed
        );

        if (response.success && response.data) {
          setPricingData({
            amounts: response.data.amounts || {
              totalAmount: 0,
              totalAmountWithoutVat: 0,
              totalVatAmount: 0,
            },
            items: response.data.items || [],
          });
        } else {
          setPricingData(null);
        }
      } catch (error) {
        console.error("Error fetching order price:", error);
        setPricingData(null);
      } finally {
        setIsPriceLoading(false);
      }
    };

    fetchOrderPrice();
  }, [
    selectedOrder,
    userSession?.token?.accessToken,
    activeWorkspace?.workspaceId,
  ]);

  return (
    <PageLayout>
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-text">
            {t("common:incoming_orders")}
          </h1>
        </div>

        {!loading && orders.length === 0 ? (
          <EmptyState
            icon={<HiInbox className="w-16 h-16 text-muted" />}
            title={t("orders:no_incoming_orders_title")}
            description={t("orders:no_incoming_orders_description")}
          />
        ) : (
          <SplitTable
            ref={splitTableRef}
            tableRows={tableRows}
            tableHeaders={tableHeaders}
            isLoading={loading}
            onRowClick={handleRowClick}
            content={
              <IncomingOrderDetails
                order={selectedOrder}
                pricingData={pricingData}
                isPriceLoading={isPriceLoading}
                isRejecting={isRejecting}
                isConfirming={isConfirming}
                onEditOrder={handleEditOrder}
                onRejectOrder={handleRejectOrder}
                onApproveOrder={handleApproveOrder}
              />
            }
            topRightContent={
              selectedOrder ? (
                <span className="text-sm text-muted">
                  {t("common:received")}:{" "}
                  {formatGmailStyle(selectedOrder.createdAt, t("common:no_date"))}
                </span>
              ) : null
            }
            tableWidth="w-full lg:w-2/5"
            contentWidth="lg:w-3/5"
          />
        )}

        {/* Order Confirmation Modal */}
        <OrderConfirmationModal
          show={showConfirmationModal}
          isLoading={isConfirming}
          orderCustomer={selectedOrder?.customer}
          initialCustomerId={selectedOrder?.customerId}
          initialDriverId={selectedOrder?.driverId}
          onClose={handleCloseConfirmationModal}
          onConfirm={handleConfirmOrder}
        />

        {/* Delete Order Dialog */}
        <DialogNotify
          show={showDeleteOrderDialog}
          isLoading={false}
          icon={<DeleteOrderIcon />}
          buttonCloseText={t("common:close")}
          buttonConfirmColor="red300"
          buttonConfirmText={t("orders:all_orders.delete")}
          title={t("orders:all_orders.delete_title")}
          text={t("orders:all_orders.delete_description")}
          toggleModal={() => setShowDeleteOrderDialog(false)}
          confirmButton={deleteOrder}
        />

        {/* Address Validation Error Toast */}
        <ValidationToast
          show={showAddressValidationToast}
          title={t("orders:address_validation_error")}
          message={addressValidationMessage}
          onClose={() => setShowAddressValidationToast(false)}
          position="top-right"
        />

        {/* Success Toast */}
        {/* Removed as per edit hint */}
      </div>
    </PageLayout>
  );
};

export default IncomingOrders;
